import styles from './styles.module.css'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../global/components/buttons/secondary-button/SecondaryButton'
import { useState, ChangeEvent } from 'react'
import { setTabulationArea } from '../../api'
import { fetchData } from '../../../../../global/utils/fetch'
import {
  ITabulationAreasDataProps,
  ITabulationAreasProps,
} from '../container-modal/LocationModal'
import { ITabulationArea } from '../../../../../../../app/entities/TabulationArea'

interface IZipCodeConflictResponse {
  conflict?: boolean
  zipCode?: string
  locationName?: string
  locationId?: string
  affectedOrders?: Array<{ objectId: string; workOrderId: number; zipCode: string }>
}

interface IZipCodeRowProps {
  cancel: React.MouseEventHandler
  tabulationAreasProps: ITabulationAreasProps
  locationId: string
}

export default function CreateZipCodeRow(props: IZipCodeRowProps) {
  const { tabulationAreasData, setTabulationAreasData } =
    props.tabulationAreasProps
  const [newTabArea, setNewTabArea] = useState<
    Pick<ITabulationArea, 'zipCode' | 'stateAbbr' | 'status'>
  >({
    zipCode: '',
    stateAbbr: '',
    status: '',
  })

  const [conflictDialog, setConflictDialog] = useState<{
    open: boolean
    conflict: IZipCodeConflictResponse | null
  }>({
    open: false,
    conflict: null,
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewTabArea({ ...newTabArea, [e.target.name]: e.target.value })
  }

  const addNewTabulationArea = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    const { zipCode, stateAbbr, status } = newTabArea

    if (!zipCode || !stateAbbr || !status) {
      alert('Missing fields required to add new zip code')
      return
    }

    try {
      const request = setTabulationArea(props.locationId, newTabArea, false)
      const response: IZipCodeConflictResponse = await fetchData(request)

      if (response?.conflict) {
        setConflictDialog({
          open: true,
          conflict: response,
        })
        return
      }

      setTabulationAreasData((prevState: ITabulationAreasDataProps) => {
        const createdTabArea = [...prevState.created, newTabArea]
        const newTabAreas = [...prevState.tabulationAreas, newTabArea]
        return {
          ...prevState,
          tabulationAreas: newTabAreas,
          created: createdTabArea,
        }
      })

      props.cancel(e)
    } catch (error) {
      console.error('Error adding zip code:', error)
      alert('Error adding zip code. Please try again.')
    }
  }

  const handleConfirmZipReassignment = async () => {
    try {
      const request = setTabulationArea(props.locationId, newTabArea, true)
      const response: IZipCodeConflictResponse = await fetchData(request)

      if (response?.conflict || response == null) {
        alert('Failed to reassign zip code.')
        return
      }

      setTabulationAreasData((prevState: ITabulationAreasDataProps) => {
        const createdTabArea = [...prevState.created, newTabArea]
        const newTabAreas = [...prevState.tabulationAreas, newTabArea]
        return {
          ...prevState,
          tabulationAreas: newTabAreas,
          created: createdTabArea,
        }
      })

      setConflictDialog({ open: false, conflict: null })
      props.cancel({ preventDefault: () => {} } as React.MouseEvent)
    } catch (error) {
      console.error('Error reassigning zip code:', error)
      alert('Error reassigning zip code. Please try again.')
    }
  }

  const handleCancelZipReassignment = () => {
    setConflictDialog({ open: false, conflict: null })
  }

  return (
    <>
      <tr className={styles.bodyRow}>
        <td className={styles.bodyRowCell}>
          <TextField
            label='Zip Code'
            name='zipCode'
            size='small'
            onChange={handleChange}
          />
        </td>
        <td className={styles.bodyRowCell}>
          <TextField
            label='State Abbr'
            name='stateAbbr'
            size='small'
            onChange={handleChange}
          />
        </td>
        <td className={styles.bodyRowCell}>
          <TextField
            label='Status'
            name='status'
            size='small'
            onChange={handleChange}
          />
        </td>
        <td className={styles.bodyRowCell}>
          <div className={styles.buttonCell}>
            <a
              onClick={props.cancel}
              className={styles.buttonText}
            >
              Cancel
            </a>
            <div className={styles.line}></div>
            <a
              className={styles.buttonText}
              onClick={addNewTabulationArea}
            >
              Add
            </a>
          </div>
        </td>
      </tr>

      {/* Conflict Dialog */}
      <Dialog
        open={conflictDialog.open}
        onClose={handleCancelZipReassignment}
        aria-labelledby='zip-conflict-dialog-title'
        PaperProps={{
          style: {
            borderRadius: '8px',
            padding: '16px',
            maxWidth: '500px',
          },
        }}
      >
        <DialogTitle
          id='zip-conflict-dialog-title'
          style={{ fontWeight: 600 }}
        >
          Zip Code Conflict Detected
        </DialogTitle>
        <DialogContent>
          <p>This zip code already belongs to another location:</p>
          {conflictDialog.conflict && (
            <div
              style={{
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
            >
              <p style={{ margin: 0 }}>
                <strong>{conflictDialog.conflict.zipCode}</strong> is assigned
                to <strong>{conflictDialog.conflict.locationName}</strong>
              </p>
            </div>
          )}
          {conflictDialog.conflict?.affectedOrders &&
            conflictDialog.conflict.affectedOrders.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  The following orders will be bumped to pending_rescheduling:
                </p>
                <div
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                  }}
                >
                  {conflictDialog.conflict.affectedOrders
                    .map((o) => `${o.workOrderId} (${o.zipCode})`)
                    .join(', ')}
                </div>
              </div>
            )}
          <p>
            Are you sure you want to reassign this zip code to this location?
          </p>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <SecondaryButton
            onClick={handleCancelZipReassignment}
            buttonName='Cancel'
            style={{ marginRight: '12px' }}
          />
          <PrimaryButton
            onClick={handleConfirmZipReassignment}
            buttonName='Yes, Reassign Zip Code'
          />
        </DialogActions>
      </Dialog>
    </>
  )
}
